import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { getUsersInternalTableDataRetrieveQueryKey } from "gen/endpoints/internal/internal";
import { useUserCreate } from "gen/endpoints/users/users";
import { UserCreateDataRequest } from "gen/model/userCreateDataRequest";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export interface InternalAddUserModalProps {
  onCloseComplete(): void;
}

export const InternalAddUserModal: React.FC<InternalAddUserModalProps> = ({
  onCloseComplete,
}) => {
  const { t } = useTranslation(["internal", "auth", "common"]);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const { mutateAsync: createUser } = useUserCreate();

  const form = useForm<UserCreateDataRequest>();

  const invalidateQueries = useInvalidateQueryKeys();
  const onSubmit = async (data: UserCreateDataRequest) => {
    await createUser({ data });
    await invalidateQueries(
      getUsersInternalTableDataRetrieveQueryKey({
        edits_enabled: false,
      }),
      getUsersInternalTableDataRetrieveQueryKey({
        edits_enabled: true,
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onClose}
          successMessage={t("internal:modal.createUser.success")}
        >
          <ModalHeader>
            {t("internal:modal.createUser.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormTextInput title={t("auth:emailAddress")} name="email" />
            <FormTextInput
              type="password"
              title={t("auth:password")}
              name="password"
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={<FormSubmitButton>{t("common:save")}</FormSubmitButton>}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
